import { FC, useRef, useState, useCallback, useEffect, useContext, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Stack,
  Grid,
  IconButton,
  Tooltip,
  Button,
  Select,
  MenuItem,
  OutlinedInput,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Delete, ImageOutlined, Settings, Remove, OpenInNew, Edit, Check, Close } from '@mui/icons-material';
import moment from 'moment';
import { getNextPublishingDate as computeNextPublishingDate, isPublishingDateOverdue } from '../../../../../../utils/automationSchedule';
import { API_SERVER } from '../../../../../../contants';
import apis from '../../../../../../apis';
import { AppContext } from '../../../../../../context';
import { useTheme } from '@mui/material/styles';
import { CellConfigDialog } from '../../../../../../components/Dialogs/CellConfig';
import { TableConfigurationDialog, TableData, LegacyTableData } from '../../../widgets';
import { configWrapperStyles, configFieldStyles } from '../../../widgets/TableConfigurationDialog/styles';
import { SectionHeaders, SECTION_HEADER_DEFAULTS } from '../PdfPreview';
import {
  normalizeMonthlyCommentary,
  DEFAULT_VERTREIBSZULASSUNG,
  splitMepreisInPreise,
  getMonthlyCommentaryMaxChars,
  type ShareClassColumn,
  type ShareClassVisibilityMode,
} from '../mappers';
import { isPvmGpsTemplate } from '../../69d0c43f5a2b1c0d9e8f7a6b/templateConstants';
import { PVM_PAGE2_LEFT_IMAGE_LABEL, PVM_PAGE2_LEFT_IMAGE_LABEL_2, PVM_PAGE2_LEFT_IMAGE_LABEL_3 } from '../../69d0c43f5a2b1c0d9e8f7a6b/templateConstants';
import {
  scrollElementIntoScrollableColumn,
  PDF_CONFIG_PARENT_DEBOUNCE_MS,
} from '../../../../debugMonthlyCommentary';
import { TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE, TEMPLATE_ID_DUAL_SHARE_CLASS } from '../templateConstants';
import { deriveTitleAdditionFromShareClassColumns } from '../titleAddition';
import { getStammdatenFormLabel } from '../stammdatenFieldLabels';
import { KONDITIONEN_TER_FORM_LABEL } from '../konditionenFieldLabels';
import { getDefaultPieChartHeading, type PieChartHeadingKey } from '../pieChartHeadings';
import { getPieChartConfigOrder } from '../pieChartDisplay';
import { EsgFundImport } from '../EsgFundImport';
import { templateShowsAnlageberater } from '../anlageberater';
import { syncValuationDatesFromPublishedAt } from '../factsheetDates';

interface PdfConfigProps {
  data: any;
  onChange: (data: any) => void;
  /** Called with flush() so parent can sync pending debounced edits before Save */
  onRegisterFlush?: (flush: () => void) => void;
  isAutomated?: boolean;
  lastPublishedAt?: string | null;
  automationFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
}

export const PdfConfig: FC<PdfConfigProps> = ({
  data,
  onChange,
  onRegisterFlush,
  isAutomated = false,
  lastPublishedAt = null,
  automationFrequency = 'daily',
}) => {
  const { document } = useContext(AppContext);
  /** Dual-column config tabs for this template; do not require 2 template ISINs (second column can use Stammdaten-ISIN). */
  const dualShareEnabled = document?.template?._id === TEMPLATE_ID_DUAL_SHARE_CLASS;
  const templateId = document?.template?._id as string | undefined;
  const isPvmGps = isPvmGpsTemplate(templateId);
  const omitEthikKontroverse =
    document?.template?._id === TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE || isPvmGps;
  const page2ImageInputRef = useRef<HTMLInputElement | null>(null);
  const page2Image2InputRef = useRef<HTMLInputElement | null>(null);
  const page2Image3InputRef = useRef<HTMLInputElement | null>(null);
  const manImageInputRef = useRef<HTMLInputElement | null>(null);
  const configPieTitle = (key: PieChartHeadingKey) => getDefaultPieChartHeading(templateId, key);
  const [preiseTab, setPreiseTab] = useState(0);
  const [konditionenTab, setKonditionenTab] = useState(0);
  const [stammdatenTab, setStammdatenTab] = useState(0);
  const theme = useTheme();
  const [formData, setFormData] = useState(data || {});
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const [performanceDialogOpen, setPerformanceDialogOpen] = useState(false);
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [editingHeader, setEditingHeader] = useState<{ key: keyof SectionHeaders; value: string } | null>(null);
  const [openCellConfigDialog, setOpenCellConfigDialog] = useState<{
    section: string;
    field: string;
    shareClassTab?: number;
  } | null>(null);
  const [monthlyCommentaryOverflow, setMonthlyCommentaryOverflow] = useState({ left: false, right: false });

  /** Latest form snapshot (synced for debounced → parent push after render) */
  const formDataRef = useRef(formData);
  /** Single debounce timer for all text-style updates → parent preview */
  const parentSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollAfterParentSyncRef = useRef(false);

  const clearParentSyncTimer = useCallback(() => {
    if (parentSyncTimerRef.current) {
      clearTimeout(parentSyncTimerRef.current);
      parentSyncTimerRef.current = null;
    }
  }, []);

  /** Push debounced edits to parent immediately (blur / before structural changes / Save) */
  const flushPendingParentSync = useCallback(() => {
    if (!parentSyncTimerRef.current) return;
    clearTimeout(parentSyncTimerRef.current);
    parentSyncTimerRef.current = null;
    scrollAfterParentSyncRef.current = false;
    onChange(formDataRef.current);
  }, [onChange]);

  const scheduleParentSync = useCallback(
    (scrollToMonthly?: boolean) => {
      if (parentSyncTimerRef.current) {
        clearTimeout(parentSyncTimerRef.current);
        parentSyncTimerRef.current = null;
      }
      scrollAfterParentSyncRef.current = !!scrollToMonthly;
      parentSyncTimerRef.current = setTimeout(() => {
        parentSyncTimerRef.current = null;
        const shouldScroll = scrollAfterParentSyncRef.current;
        scrollAfterParentSyncRef.current = false;
        onChange(formDataRef.current);
        if (shouldScroll) {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              const el = window.document.getElementById('monthly-commentary-section');
              if (el) scrollElementIntoScrollableColumn(el);
            });
          });
        }
      }, PDF_CONFIG_PARENT_DEBOUNCE_MS);
    },
    [onChange],
  );

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (!onRegisterFlush) return;
    onRegisterFlush(() => flushPendingParentSync());
    return () => {
      onRegisterFlush(() => {
        /* unregister: parent replaces flush ref */
      });
    };
  }, [onRegisterFlush, flushPendingParentSync]);

  useEffect(
    () => () => {
      clearParentSyncTimer();
    },
    [clearParentSyncTimer],
  );

  // Get ISIN from document template
  const isin = document?.template?.isins?.[0] || formData?.historyIsin || '';

  const sectionHeaders = useMemo<SectionHeaders>(() => ({
    ...SECTION_HEADER_DEFAULTS,
    ...(formData.sectionHeaders || {}),
  }), [formData.sectionHeaders]);

  useEffect(() => {
    if (!data) {
      setFormData({});
      return;
    }
    // Skip echo-back from debounced parent sync — avoids cursor jumps while typing (incl. multiline).
    try {
      if (JSON.stringify(data) === JSON.stringify(formDataRef.current)) {
        return;
      }
    } catch {
      /* non-serializable values — fall through */
    }
    if (!dualShareEnabled) {
      setFormData(data);
      return;
    }
    const migratedPreise = splitMepreisInPreise(data.preise || {});
    const migratedCols = Array.isArray(data.shareClassColumns)
      ? data.shareClassColumns.map((col: ShareClassColumn) => ({
          ...col,
          preise: splitMepreisInPreise(col.preise as Record<string, unknown>),
        }))
      : data.shareClassColumns;
    const next = {
      ...data,
      preise: migratedPreise,
      shareClassColumns: migratedCols,
    };
    setFormData(next);
    formDataRef.current = next;
    try {
      if (JSON.stringify(next) !== JSON.stringify(data)) {
        onChange(next);
      }
    } catch {
      onChange(next);
    }
  }, [data, dualShareEnabled, onChange]);

  // Section header editing & scroll-to-section (preview uses rightWrapper overflow:auto — must scroll that pane)
  const handleScrollTo = (sectionId: string) => {
    const element = window.document.getElementById(sectionId);
    if (!element) return;
    scrollElementIntoScrollableColumn(element);
  };

  const handleSectionHeaderChange = useCallback((key: keyof SectionHeaders, value: string) => {
    flushPendingParentSync();
    setFormData((prev: any) => {
      const updatedHeaders: SectionHeaders = {
        ...SECTION_HEADER_DEFAULTS,
        ...(prev.sectionHeaders || {}),
        [key]: value,
      };
      const updatedData = { ...prev, sectionHeaders: updatedHeaders };
      formDataRef.current = updatedData;
      onChange(updatedData);
      return updatedData;
    });
    setEditingHeader(null);
  }, [onChange, flushPendingParentSync]);

  const handleConfirmSectionHeaderEdit = useCallback(() => {
    if (!editingHeader) return;
    handleSectionHeaderChange(editingHeader.key, editingHeader.value);
  }, [editingHeader, handleSectionHeaderChange]);

  const renderSectionHeaderControls = (
    key: keyof SectionHeaders,
    options: { variant?: 'subtitle1' | 'subtitle2'; sectionId?: string; fontWeight?: number | string } = {},
  ) => {
    const { variant = 'subtitle1', sectionId, fontWeight = 'bold' } = options;
    const isEditing = editingHeader?.key === key;

    return (
      <Box display="flex" alignItems="center" gap={2}>
        {isEditing ? (
          <>
            <TextField
              value={editingHeader?.value || ''}
              onChange={(e) => setEditingHeader((prev) => (prev ? { ...prev, value: e.target.value } : prev))}
              size="small"
              autoFocus
              InputProps={{ sx: { fontWeight } }}
            />
            <Check
              fontSize="small"
              color="secondary"
              onClick={handleConfirmSectionHeaderEdit}
              sx={{ width: 20, height: 20, cursor: 'pointer', transition: 'color 120ms ease', '&:hover': { color: 'secondary.dark' } }}
            />
            <Close
              fontSize="small"
              color="secondary"
              onClick={() => setEditingHeader(null)}
              sx={{ width: 20, height: 20, cursor: 'pointer', transition: 'color 120ms ease', '&:hover': { color: 'secondary.dark' } }}
            />
          </>
        ) : (
          <>
            <Typography variant={variant} fontWeight={fontWeight}>
              {sectionHeaders[key] ?? SECTION_HEADER_DEFAULTS[key] ?? ''}
            </Typography>
            <Edit
              fontSize="small"
              color="secondary"
              onClick={() =>
                setEditingHeader({
                  key,
                  value: sectionHeaders[key] ?? SECTION_HEADER_DEFAULTS[key] ?? '',
                })
              }
              sx={{ width: 20, height: 20, cursor: 'pointer', transition: 'color 120ms ease', '&:hover': { color: 'secondary.dark' } }}
            />
          </>
        )}
        {sectionId ? (
          <OpenInNew
            onClick={() => handleScrollTo(sectionId)}
            color="secondary"
            sx={{ width: 20, height: 20, cursor: 'pointer', transition: 'color 120ms ease', '&:hover': { color: 'secondary.dark' } }}
          />
        ) : null}
      </Box>
    );
  };

  // Default business-table icons (same order as old PDF viewer)
  const DEFAULT_ESG_ICONS = [
    './public/icon_1.png', './public/icon_8.png', './public/icon_5.png',
    './public/icon_4.png', './public/icon_10.png', './public/icon_2.png',
    './public/icon_7.png', './public/icon_3.png', './public/icon_6.png',
    './public/icon_9.png',
  ];

  const getEsgItemImage = (item: any, index: number): string => {
    if (item?.image) {
      return item.image.includes('./public') ? `${API_SERVER}/${item.image}` : item.image;
    }
    const defaultIcon = DEFAULT_ESG_ICONS[index];
    return defaultIcon ? `${API_SERVER}/${defaultIcon}` : '';
  };

  const handleImageUpload = (index: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await apis.pdf.uploadLogoImage(file);
      if (res.success) {
        const updated = [...(formData.business_table || [])];
        updated[index] = { ...updated[index], image: res.data };
        handleFieldChange('business_table', updated);
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
    }
    // Reset input so the same file can be selected again
    const inputEl = fileInputRefs.current[index];
    if (inputEl) {
      inputEl.value = '';
    }
  };

  // Field change handlers (must be defined before callbacks that reference them)
  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData((prev: any) => {
      // Published At also drives Facts & Figures header + chart until (via preise.kursper).
      // Without this sync, Save updates published_at but the badge stays on a stale kursper.
      const updatedData =
        field === 'published_at'
          ? syncValuationDatesFromPublishedAt({ ...prev }, value)
          : { ...prev, [field]: value };
      formDataRef.current = updatedData;
      const debouncePreview =
        typeof value === 'string' || typeof value === 'number';
      // Dates drive automated charts / dynamic Preise — must sync to preview immediately (not debounced like body text)
      const syncPreviewImmediately =
        field === 'published_at' || field === 'historyUntil';
      if (syncPreviewImmediately) {
        flushPendingParentSync();
        clearParentSyncTimer();
        onChange(updatedData);
      } else if (debouncePreview) {
        scheduleParentSync(false);
      } else {
        flushPendingParentSync();
        clearParentSyncTimer();
        onChange(updatedData);
      }
      return updatedData;
    });
  }, [scheduleParentSync, flushPendingParentSync, clearParentSyncTimer, onChange]);

  const handleNestedFieldChange = useCallback((parentField: string, field: string, value: any) => {
    setFormData((prev: any) => {
      const parentRaw = prev[parentField];
      const parentBase =
        parentField === 'monthlyCommentary'
          ? normalizeMonthlyCommentary(parentRaw, templateId)
          : parentRaw && typeof parentRaw === 'object' && !Array.isArray(parentRaw)
            ? { ...parentRaw }
            : {};
      const updatedData = {
        ...prev,
        [parentField]: {
          ...parentBase,
          [field]: value,
        },
      };
      formDataRef.current = updatedData;

      if (parentField === 'monthlyCommentary') {
        scheduleParentSync(true);
        return updatedData;
      }

      const debouncePreview =
        typeof value === 'string' || typeof value === 'number';
      if (debouncePreview) {
        scheduleParentSync(false);
      } else {
        flushPendingParentSync();
        clearParentSyncTimer();
        onChange(updatedData);
      }
      return updatedData;
    });
  }, [scheduleParentSync, flushPendingParentSync, clearParentSyncTimer, onChange, templateId]);
  const handleObjectFieldChange = useCallback((objectKey: string, field: string, value: any) => {
    setFormData((prev: any) => {
      const updatedData = {
        ...prev,
        [objectKey]: {
          ...(prev[objectKey] || {}),
          [field]: value,
        },
      };
      formDataRef.current = updatedData;
      const debouncePreview =
        typeof value === 'string' || typeof value === 'number';
      if (debouncePreview) {
        scheduleParentSync(false);
      } else {
        flushPendingParentSync();
        clearParentSyncTimer();
        onChange(updatedData);
      }
      return updatedData;
    });
  }, [scheduleParentSync, flushPendingParentSync, clearParentSyncTimer, onChange]);

  /** Initialize two share-class column payloads (PREISE/KONDITIONEN/STAMMDATEN per ISIN). */
  useEffect(() => {
    if (!dualShareEnabled) return;
    if (formData.shareClassColumns?.length >= 2) return;
    const isins = document?.template?.isins || [];
    const p = splitMepreisInPreise(formData.preise || {});
    const k = formData.konditionen || {};
    const s = formData.stammdaten || {};
    const nextCols: ShareClassColumn[] = [
      {
        label: 'Anteilklasse T',
        isin: String(isins[0] || ''),
        preise: { ...p },
        konditionen: { ...k },
        stammdaten: { ...s },
      },
      {
        label: 'Anteilklasse A',
        isin: String(isins[1] || ''),
        preise: { ...p },
        konditionen: { ...k },
        stammdaten: { ...s },
      },
    ];
    setFormData((prev: any) => {
      if (prev.shareClassColumns?.length >= 2) return prev;
      const updatedData = {
        ...prev,
        shareClassColumns: nextCols,
        preise: nextCols[0].preise,
        konditionen: nextCols[0].konditionen,
        stammdaten: nextCols[0].stammdaten,
        title_addition:
          prev.title_addition?.trim() ||
          deriveTitleAdditionFromShareClassColumns(nextCols),
      };
      formDataRef.current = updatedData;
      onChange(updatedData);
      return updatedData;
    });
  }, [dualShareEnabled, document?.template?.isins, formData.shareClassColumns?.length, formData.preise, formData.konditionen, formData.stammdaten, onChange]);

  const handleShareClassColumnNested = useCallback(
    (tabIdx: number, section: 'preise' | 'konditionen' | 'stammdaten', field: string, value: any) => {
      setFormData((prev: any) => {
        const cols: ShareClassColumn[] = [...(prev.shareClassColumns || [])];
        if (!cols[tabIdx]) return prev;
        const row = { ...cols[tabIdx] };
        const sec = { ...(row[section] as Record<string, unknown>), [field]: value };
        (row as any)[section] = sec;
        cols[tabIdx] = row;
        const next: any = { ...prev, shareClassColumns: cols };
        if (tabIdx === 0) {
          if (section === 'preise') next.preise = sec;
          if (section === 'konditionen') next.konditionen = sec;
          if (section === 'stammdaten') next.stammdaten = sec;
        }
        formDataRef.current = next;
        const debouncePreview = typeof value === 'string' || typeof value === 'number';
        if (debouncePreview) {
          scheduleParentSync(false);
        } else {
          flushPendingParentSync();
          clearParentSyncTimer();
          onChange(next);
        }
        return next;
      });
    },
    [scheduleParentSync, flushPendingParentSync, clearParentSyncTimer, onChange],
  );

  const handleShareClassLabelChange = useCallback(
    (tabIdx: number, label: string) => {
      setFormData((prev: any) => {
        const cols: ShareClassColumn[] = [...(prev.shareClassColumns || [])];
        if (!cols[tabIdx]) return prev;
        cols[tabIdx] = { ...cols[tabIdx], label };
        const next: any = { ...prev, shareClassColumns: cols };
        if (dualShareEnabled) {
          const derived = deriveTitleAdditionFromShareClassColumns(cols);
          const current = String(prev.title_addition || '').trim();
          const previousDerived = deriveTitleAdditionFromShareClassColumns(prev.shareClassColumns);
          if (derived && (!current || current === previousDerived)) {
            next.title_addition = derived;
          }
        }
        formDataRef.current = next;
        scheduleParentSync(false);
        return next;
      });
    },
    [dualShareEnabled, scheduleParentSync],
  );

  const getShareClassVisibilityMode = useCallback(
    (): ShareClassVisibilityMode => {
      const mode = (formData as any)?.shareClassVisibilityMode as ShareClassVisibilityMode | undefined;
      if (mode === 'column0' || mode === 'column1' || mode === 'both') return mode;
      const legacy = (formData as any)?.shareClassVisibility?.preise as ShareClassVisibilityMode | undefined;
      if (legacy === 'column0' || legacy === 'column1' || legacy === 'both') return legacy;
      return 'both';
    },
    [formData],
  );

  const handleShareClassVisibilityModeChange = useCallback(
    (mode: ShareClassVisibilityMode) => {
      setFormData((prev: any) => {
        const next = {
          ...prev,
          shareClassVisibilityMode: mode,
        };
        formDataRef.current = next;
        flushPendingParentSync();
        clearParentSyncTimer();
        onChange(next);
        return next;
      });
    },
    [clearParentSyncTimer, flushPendingParentSync, onChange],
  );

  // Performance table handlers
  const getPerformanceData = useCallback((): TableData => {
    const perf = formData.wertentwicklung_nach_perioden;
    // New V1-style structure (labels/values/columns)
    if (perf && perf.labels && Array.isArray(perf.labels) && perf.values && Array.isArray(perf.values)) {
      return perf;
    }
    // Legacy array format [{label, value}, ...]
    if (Array.isArray(perf) && perf.length > 0) {
      return {
        labels: perf.map((item: any) => item.label),
        values: [perf.map((item: any) => String(item.value))],
        columns: new Array(perf.length).fill({}).map(() => ({ type: 'text' as const })),
      };
    }
    // Default structure
    return {
      labels: ['YTD', '1 J.', '3 J.', '5 J.', '10 J.', 'seit Auflage'],
      values: [['', '', '', '', '', '']],
      columns: new Array(6).fill({}).map(() => ({ type: 'text' as const })),
    };
  }, [formData.wertentwicklung_nach_perioden]);

  const handleOpenPerformanceDialog = useCallback(() => {
    setPerformanceDialogOpen(true);
  }, []);

  const handleClosePerformanceDialog = useCallback(() => {
    setPerformanceDialogOpen(false);
  }, []);

  const handleSavePerformanceDialog = useCallback((tableData: TableData | LegacyTableData) => {
    // Always save in new format (labels/values/columns)
    const newFormatData = 'labels' in tableData ? tableData : {
      labels: tableData.headers,
      values: tableData.rows.map((row: any) => row.values),
      columns: new Array(tableData.headers.length).fill({}).map(() => ({ type: 'text' as const })),
    };
    handleFieldChange('wertentwicklung_nach_perioden', newFormatData);
  }, [handleFieldChange]);

  // Risk indicators table handlers
  const getRiskData = useCallback((): TableData => {
    const risk = formData.risikoindikatoren;
    // New V1-style structure (labels/values/columns)
    if (risk && risk.labels && Array.isArray(risk.labels) && risk.values && Array.isArray(risk.values)) {
      return risk;
    }
    // Default structure
    return {
      labels: ['', 'Fonds', 'Vergleichsindex'],
      values: [['Volatilität', '', ''], ['Max. Drawdown', '', ''], ['Sharpe Ratio', '', '']],
      columns: new Array(3).fill({}).map(() => ({ type: 'text' as const })),
    };
  }, [formData.risikoindikatoren]);

  const handleOpenRiskDialog = useCallback(() => {
    setRiskDialogOpen(true);
  }, []);

  const handleCloseRiskDialog = useCallback(() => {
    setRiskDialogOpen(false);
  }, []);

  const handleSaveRiskDialog = useCallback((tableData: TableData | LegacyTableData) => {
    const newFormatData = 'labels' in tableData ? tableData : {
      labels: tableData.headers,
      values: tableData.rows.map((row: any) => row.values),
      columns: new Array(tableData.headers.length).fill({}).map(() => ({ type: 'text' as const })),
    };
    handleFieldChange('risikoindikatoren', newFormatData);
  }, [handleFieldChange]);

  // Render an editable label-value list for chart data fields
  const renderLabelValueEditor = useCallback((
    fieldKey: string,
    title: string,
  ) => {
    const items: Array<{ label: string; value: number; color?: string }> =
      Array.isArray(formData[fieldKey]) ? formData[fieldKey] : [];

    const handleItemChange = (index: number, key: 'label' | 'value', raw: string) => {
      const updated = items.map((item, i) =>
        i === index
          ? { ...item, [key]: key === 'value' ? (raw === '' ? '' : Number(raw)) : raw }
          : item,
      );
      handleFieldChange(fieldKey, updated);
    };

    const handleAddItem = () => {
      handleFieldChange(fieldKey, [...items, { label: '', value: 0 }]);
    };

    const handleRemoveItem = (index: number) => {
      handleFieldChange(fieldKey, items.filter((_, i) => i !== index));
    };

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              {title}
            </Typography>
            <Tooltip title="Add row">
              <IconButton size="small" onClick={handleAddItem}>
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
          <Stack spacing={1}>
            {items.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="Label"
                  value={item.label}
                  onChange={(e) => handleItemChange(index, 'label', e.target.value)}
                  size="small"
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="Value (%)"
                  type="number"
                  value={item.value}
                  onChange={(e) => handleItemChange(index, 'value', e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ step: 'any' }}
                />
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            {items.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No items yet. Click + to add.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }, [formData, handleFieldChange]);

  // Calculate next publishing date
  const getNextPublishingDate = (): string => {
    if (!isAutomated) return '';
    const baseDate = lastPublishedAt ? moment(lastPublishedAt) : moment();
    if (!baseDate.isValid()) return '';
    return computeNextPublishingDate(baseDate, automationFrequency).format('YYYY-MM-DD');
  };

  const nextPublishingDate = getNextPublishingDate();

  function toDateInputValue(value?: string) {
    if (!value) return '';
    // If already YYYY-MM-DD, keep as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const iso = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
    return iso.slice(0, 10);
  }

  // --- Dynamic value helpers (prices fields) borrowed from template 68ef31... ---
  const isValueDynamic = useCallback((section: string, field: string): boolean => {
    const value = (formData as any)?.[section]?.[field];
    return typeof value === 'object' && value?.isDynamic === true;
  }, [formData]);

  const getValueDisplay = useCallback((section: string, field: string): string => {
    const value = (formData as any)?.[section]?.[field];
    if (typeof value === 'object' && value?.isDynamic) {
      return '';
    }
    return typeof value === 'string' ? value : '';
  }, [formData]);

  const handleToggleDynamic = useCallback((section: string, field: string) => {
    flushPendingParentSync();
    const currentSection = (formData as any)?.[section] || {};
    const currentValue = currentSection[field];
    const isCurrentlyDynamic = typeof currentValue === 'object' && currentValue?.isDynamic === true;

    const updatedData: any = {
      ...formData,
      [section]: {
        ...currentSection,
        [field]: isCurrentlyDynamic
          ? (typeof currentValue === 'string' ? currentValue : '')
          : {
              isDynamic: true,
              fields: [],
            },
      },
    };
    formDataRef.current = updatedData;
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange, flushPendingParentSync]);

  const handleShareClassToggleDynamic = useCallback(
    (tabIdx: number, section: 'preise' | 'konditionen' | 'stammdaten', field: string) => {
      flushPendingParentSync();
      setFormData((prev: any) => {
        const cols: ShareClassColumn[] = [...(prev.shareClassColumns || [])];
        const row = { ...cols[tabIdx] };
        const sec = { ...(row[section] as Record<string, unknown>) };
        const currentValue = sec[field];
        const isCurrentlyDynamic = typeof currentValue === 'object' && (currentValue as any)?.isDynamic === true;
        sec[field] = isCurrentlyDynamic
          ? (typeof currentValue === 'string' ? currentValue : '')
          : { isDynamic: true, fields: [] };
        (row as any)[section] = sec;
        cols[tabIdx] = row;
        const next: any = { ...prev, shareClassColumns: cols };
        if (tabIdx === 0) {
          if (section === 'preise') next.preise = sec;
          if (section === 'konditionen') next.konditionen = sec;
          if (section === 'stammdaten') next.stammdaten = sec;
        }
        formDataRef.current = next;
        onChange(next);
        return next;
      });
    },
    [onChange, flushPendingParentSync],
  );

  const handleOpenCellConfig = useCallback((section: string, field: string, shareClassTabIdx?: number) => {
    setOpenCellConfigDialog({ section, field, shareClassTab: shareClassTabIdx });
  }, []);

  const handleCloseCellConfig = useCallback(() => {
    setOpenCellConfigDialog(null);
  }, []);

  const handleSetCellConfig = useCallback((config: any) => {
    if (!openCellConfigDialog) return;
    flushPendingParentSync();
    const { section, field, shareClassTab: tabIdx } = openCellConfigDialog;
    setFormData((prev: any) => {
      if (tabIdx !== undefined && dualShareEnabled) {
        const cols: ShareClassColumn[] = [...(prev.shareClassColumns || [])];
        const row = { ...cols[tabIdx] };
        const sec = { ...(row[section as keyof ShareClassColumn] as Record<string, unknown>), [field]: config };
        (row as any)[section] = sec;
        cols[tabIdx] = row;
        const updatedData: any = { ...prev, shareClassColumns: cols };
        if (tabIdx === 0) {
          if (section === 'preise') updatedData.preise = sec;
          if (section === 'konditionen') updatedData.konditionen = sec;
          if (section === 'stammdaten') updatedData.stammdaten = sec;
        }
        formDataRef.current = updatedData;
        onChange(updatedData);
        return updatedData;
      }
      const updatedData = {
        ...prev,
        [section]: {
          ...(prev[section] || {}),
          [field]: config,
        },
      };
      formDataRef.current = updatedData;
      onChange(updatedData);
      return updatedData;
    });
    setOpenCellConfigDialog(null);
  }, [openCellConfigDialog, onChange, flushPendingParentSync, dualShareEnabled]);

  const renderDynamicValueField = useCallback((
    section: string,
    field: string,
    label: string,
    placeholder: string
  ) => {
    const isDynamic = isValueDynamic(section, field);
    const value = getValueDisplay(section, field);
    const config = (formData as any)?.[section]?.[field];

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          {isDynamic ? (
            <Box
              sx={{
                ...configWrapperStyles,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',
                flexWrap: 'wrap',
              }}
            >
              {config?.fields?.map((fieldConfig: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    ...configFieldStyles(theme, fieldConfig.type),
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {fieldConfig.type === 'text'
                      ? fieldConfig.text
                      : `${fieldConfig.table || '(table)'}.${fieldConfig.field || '(field)'}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <TextField
              label={label}
              value={value || placeholder}
              onChange={(e) => handleNestedFieldChange(section, field, e.target.value)}
              fullWidth
              size="small"
            />
          )}
        </Box>
        {isDynamic && (
          <IconButton
            size="small"
            onClick={() => handleOpenCellConfig(section, field)}
            sx={{ width: 32, height: 32 }}
          >
            <Settings fontSize="small" />
          </IconButton>
        )}
        <Tooltip title="Toggle dynamic value">
          <FormControlLabel
            control={
              <Switch
                checked={isDynamic}
                onChange={() => handleToggleDynamic(section, field)}
                color="secondary"
                size="small"
              />
            }
            label="Dynamic"
            sx={{ m: 0, minWidth: 80 }}
          />
        </Tooltip>
      </Box>
    );
  }, [formData, getValueDisplay, handleNestedFieldChange, handleOpenCellConfig, handleToggleDynamic, isValueDynamic, theme]);

  const isShareClassValueDynamic = useCallback(
    (tab: number, section: 'preise' | 'konditionen' | 'stammdaten', field: string): boolean => {
      const value = (formData as any)?.shareClassColumns?.[tab]?.[section]?.[field];
      return typeof value === 'object' && value?.isDynamic === true;
    },
    [formData],
  );

  const getShareClassValueDisplay = useCallback(
    (tab: number, section: 'preise' | 'konditionen' | 'stammdaten', field: string): string => {
      const value = (formData as any)?.shareClassColumns?.[tab]?.[section]?.[field];
      if (typeof value === 'object' && value?.isDynamic) return '';
      return typeof value === 'string' ? value : '';
    },
    [formData],
  );

  const renderShareClassDynamicValueField = useCallback(
    (
      tabIdx: number,
      section: 'preise' | 'konditionen' | 'stammdaten',
      field: string,
      label: string,
      placeholder: string,
    ) => {
      const isDynamic = isShareClassValueDynamic(tabIdx, section, field);
      const value = getShareClassValueDisplay(tabIdx, section, field);
      const config = (formData as any)?.shareClassColumns?.[tabIdx]?.[section]?.[field];

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            {isDynamic ? (
              <Box
                sx={{
                  ...configWrapperStyles,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  flexWrap: 'wrap',
                }}
              >
                {config?.fields?.map((fieldConfig: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      ...configFieldStyles(theme, fieldConfig.type),
                      maxWidth: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      noWrap
                      sx={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {fieldConfig.type === 'text'
                        ? fieldConfig.text
                        : `${fieldConfig.table || '(table)'}.${fieldConfig.field || '(field)'}`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <TextField
                label={label}
                value={value || placeholder}
                onChange={(e) => handleShareClassColumnNested(tabIdx, section, field, e.target.value)}
                fullWidth
                size="small"
              />
            )}
          </Box>
          {isDynamic && (
            <IconButton
              size="small"
              onClick={() => handleOpenCellConfig(section, field, tabIdx)}
              sx={{ width: 32, height: 32 }}
            >
              <Settings fontSize="small" />
            </IconButton>
          )}
          <Tooltip title="Toggle dynamic value">
            <FormControlLabel
              control={
                <Switch
                  checked={isDynamic}
                  onChange={() => handleShareClassToggleDynamic(tabIdx, section, field)}
                  color="secondary"
                  size="small"
                />
              }
              label="Dynamic"
              sx={{ m: 0, minWidth: 80 }}
            />
          </Tooltip>
        </Box>
      );
    },
    [
      formData,
      getShareClassValueDisplay,
      handleShareClassColumnNested,
      handleOpenCellConfig,
      handleShareClassToggleDynamic,
      isShareClassValueDynamic,
      theme,
    ],
  );

  const renderShareClassTabs = useCallback(
    (
      value: number,
      onChange: (next: number) => void,
    ) => {
      const active = formData.shareClassColumns?.[value];
      return (
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Tabs
            value={value}
            onChange={(_, v) => {
              flushPendingParentSync();
              onChange(v);
            }}
          >
            <Tab label={formData.shareClassColumns?.[0]?.label || 'Anteilklasse 1'} />
            <Tab label={formData.shareClassColumns?.[1]?.label || 'Anteilklasse 2'} />
          </Tabs>
          <TextField
            label="Spaltenüberschrift (PDF)"
            value={active?.label || ''}
            onChange={(e) => handleShareClassLabelChange(value, e.target.value)}
            fullWidth
            size="small"
          />
        </Stack>
      );
    },
    [document?.template?.isins, flushPendingParentSync, formData.shareClassColumns, handleShareClassLabelChange],
  );

  return (
    <Box sx={{ p: 2, my: 64, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography variant="h6" gutterBottom>
        PDF Configuration
      </Typography>

      {/* Header Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Header Configuration
          </Typography>
          <Stack spacing={4}>
            <TextField
              label="Title"
              value={formData.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Title Addition"
              value={formData.title_addition || ''}
              onChange={(e) => handleFieldChange('title_addition', e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g. T + A or R"
              helperText="Share class suffix shown after the fund name in the header (e.g. T + A). Leave empty for ETF."
            />
            <TextField
              label={isAutomated ? 'Last Published At' : 'Published At'}
              type="date"
              value={
                isAutomated && lastPublishedAt
                  ? toDateInputValue(lastPublishedAt)
                  : toDateInputValue(formData.published_at)
              }
              onChange={(e) => {
                if (!isAutomated) {
                  handleFieldChange('published_at', e.target.value);
                }
              }}
              disabled={isAutomated}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              helperText={
                isAutomated
                  ? 'Automated documents are published automatically by the system'
                  : 'Also updates the Facts & Figures header date and chart as-of (preise.kursper).'
              }
            />
            {isAutomated && nextPublishingDate && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Next publication at {moment(nextPublishingDate).format('DD.MM.YYYY')}
                {isPublishingDateOverdue(nextPublishingDate) ? ' (overdue)' : ''}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Monthly Commentary — directly under header, full width like other cards */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('monthlyCommentary', { sectionId: 'monthly-commentary-section' })}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {(['left', 'right'] as const).map((side) => {
              const commentary = normalizeMonthlyCommentary(formData.monthlyCommentary, templateId);
              const maxChars = getMonthlyCommentaryMaxChars(templateId, side);
              const length = commentary[side].length;
              const showLimitError = monthlyCommentaryOverflow[side];
              const label = side === 'left' ? 'Left Part' : 'Right Part';
              const placeholder = side === 'left' ? 'Enter left column text...' : 'Enter right column text...';
              return (
                <Grid item xs={12} sm={6} key={side}>
                  <TextField
                    label={label}
                    value={commentary[side]}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setMonthlyCommentaryOverflow((prev) => ({
                        ...prev,
                        [side]: raw.length > maxChars,
                      }));
                      handleNestedFieldChange(
                        'monthlyCommentary',
                        side,
                        raw.slice(0, maxChars),
                      );
                    }}
                    onBlur={flushPendingParentSync}
                    fullWidth
                    multiline
                    rows={10}
                    size="small"
                    placeholder={placeholder}
                    helperText={`${length}/${maxChars}`}
                    FormHelperTextProps={{
                      sx: { color: showLimitError ? 'error.main' : 'text.secondary' },
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {isPvmGps ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Page 1 — Monatskommentar Bild (wie SHV)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              Optional custom image next to the Monatskommentar (same role as the SHV portrait). Leave empty for text-only.
            </Typography>
            {formData.manImageUrl ? (
              <Box
                component="img"
                src={formData.manImageUrl}
                alt="Monatskommentar"
                sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', display: 'block', mb: 1.5 }}
              />
            ) : null}
            <input
              ref={manImageInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const res = await apis.pdf.uploadLogoImage(file);
                  if (!res?.success || !res.data) {
                    console.error('Failed to upload man image: unexpected response', res);
                    return;
                  }
                  let imageUrl = String(res.data);
                  if (imageUrl.includes('./public')) {
                    imageUrl = `${API_SERVER}/${imageUrl}`;
                  } else if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
                    imageUrl = `https://cixon-finance-docs.s3.amazonaws.com/${imageUrl}`;
                  }
                  handleFieldChange('manImageUrl', imageUrl);
                } catch (err) {
                  console.error('Failed to upload man image', err);
                } finally {
                  e.target.value = '';
                }
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              {formData.manImageUrl ? (
                <Button size="small" variant="outlined" onClick={() => manImageInputRef.current?.click()}>
                  Replace
                </Button>
              ) : (
                <Button size="small" variant="outlined" onClick={() => manImageInputRef.current?.click()}>
                  Upload
                </Button>
              )}
              {formData.manImageUrl ? (
                <Button size="small" color="inherit" onClick={() => handleFieldChange('manImageUrl', null)}>
                  Clear
                </Button>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {/* Investment Philosophy */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('investmentphilosophie', { sectionId: 'investmentphilosophie-section' })}
          <TextField
            label="Investment Philosophy"
            value={formData.investmentphilosophie || ''}
            onChange={(e) => handleFieldChange('investmentphilosophie', e.target.value)}
            fullWidth
            multiline
            rows={6}
            size="small"
          />
        </CardContent>
      </Card>

      {/* Fußnote */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Fußnote
          </Typography>
          <TextField
            label="Fußnote"
            value={formData.fussnote || ''}
            onChange={(e) => handleFieldChange('fussnote', e.target.value)}
            fullWidth
            multiline
            rows={4}
            size="small"
            placeholder="Enter footnote text..."
            helperText="One line per footnote. Use ¹, ², etc. for superscript markers."
          />
        </CardContent>
      </Card>

      {isPvmGps ? (
        <>
          {(
            [
              {
                field: 'page2ImageUrl' as const,
                title: 'Page 2 — Grafik / Fondsmanager-Foto',
                label: PVM_PAGE2_LEFT_IMAGE_LABEL,
                inputRef: page2ImageInputRef,
              },
              {
                field: 'page2ImageUrl2' as const,
                title: 'Page 2 — Zusatzgrafik',
                label: PVM_PAGE2_LEFT_IMAGE_LABEL_2,
                inputRef: page2Image2InputRef,
              },
              {
                field: 'page2ImageUrl3' as const,
                title: 'Page 2 — Weitere Grafik',
                label: PVM_PAGE2_LEFT_IMAGE_LABEL_3,
                inputRef: page2Image3InputRef,
              },
            ] as const
          ).map(({ field, title, label, inputRef }) => (
            <Card key={field} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {title}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  {label}. Upload an image to replace the placeholder on page 2 (left column).
                </Typography>
                {formData[field] ? (
                  <Box
                    component="img"
                    src={formData[field]}
                    alt={title}
                    sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', display: 'block', mb: 1.5 }}
                  />
                ) : null}
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const res = await apis.pdf.uploadLogoImage(file);
                      if (!res?.success || !res.data) {
                        console.error('Failed to upload page-2 image: unexpected response', res);
                        return;
                      }
                      let imageUrl = String(res.data);
                      if (imageUrl.includes('./public')) {
                        imageUrl = `${API_SERVER}/${imageUrl}`;
                      } else if (!imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
                        imageUrl = `https://cixon-finance-docs.s3.amazonaws.com/${imageUrl}`;
                      }
                      handleFieldChange(field, imageUrl);
                    } catch (err) {
                      console.error('Failed to upload page-2 image', err);
                    } finally {
                      e.target.value = '';
                    }
                  }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  {formData[field] ? (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => handleFieldChange(field, null)}
                    >
                      Remove image
                    </Button>
                  ) : null}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ImageOutlined />}
                    onClick={() => inputRef.current?.click()}
                  >
                    Upload image
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </>
      ) : null}

      {/* Disclaimer */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Disclaimer
          </Typography>
          <TextField
            label="Disclaimer"
            value={formData.disclaimer || ''}
            onChange={(e) => handleFieldChange('disclaimer', e.target.value)}
            fullWidth
            multiline
            rows={8}
            size="small"
            placeholder="Enter disclaimer text..."
          />
        </CardContent>
      </Card>

      {dualShareEnabled ? (
        <>
      {/* Prices (dual) */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  {renderSectionHeaderControls('preise', { sectionId: 'preise-section' })}
                </Box>
                <Select
                  value={getShareClassVisibilityMode()}
                  onChange={(e) =>
                    handleShareClassVisibilityModeChange(e.target.value as ShareClassVisibilityMode)
                  }
                  size="small"
                  sx={{ minWidth: 280, flexShrink: 0 }}
                >
                  <MenuItem value="both">Show both columns</MenuItem>
                  <MenuItem value="column0">Show only first column</MenuItem>
                  <MenuItem value="column1">Show only second column</MenuItem>
                </Select>
              </Box>
              {renderShareClassTabs(preiseTab, setPreiseTab)}
              <Stack spacing={4}>
                <TextField
                  label="Währung"
                  value={(formData.shareClassColumns?.[preiseTab]?.preise?.wahrung as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(preiseTab, 'preise', 'wahrung', e.target.value)}
                  fullWidth
                  size="small"
                />
                {renderShareClassDynamicValueField(preiseTab, 'preise', 'ausgabepreis', 'Ausgabepreis', '')}
                {renderShareClassDynamicValueField(preiseTab, 'preise', 'ruecknahmepreis', 'Rücknahmepreis', '')}
                {renderShareClassDynamicValueField(preiseTab, 'preise', 'fondsvolumen', 'Fondsvolumen', '')}
                <TextField
                  label="Ertragsart"
                  placeholder="Thesaurierend"
                  value={(formData.shareClassColumns?.[preiseTab]?.preise?.ertragsart as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(preiseTab, 'preise', 'ertragsart', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Letzte Ausschüttung"
                  value={(formData.shareClassColumns?.[preiseTab]?.preise?.letzte as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(preiseTab, 'preise', 'letzte', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Ausschüttungstermin"
                  value={(formData.shareClassColumns?.[preiseTab]?.preise?.ausschuttung as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(preiseTab, 'preise', 'ausschuttung', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Conditions (dual) */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              {renderSectionHeaderControls('konditionen', { sectionId: 'konditionen-section' })}
              {renderShareClassTabs(konditionenTab, setKonditionenTab)}
              <Stack spacing={4}>
                <TextField
                  label="Laufende Kosten"
                  value={(formData.shareClassColumns?.[konditionenTab]?.konditionen?.laufendeKosten as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(konditionenTab, 'konditionen', 'laufendeKosten', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Verwaltungsgebühr"
                  value={(formData.shareClassColumns?.[konditionenTab]?.konditionen?.verwaltungsgebühr as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(konditionenTab, 'konditionen', 'verwaltungsgebühr', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Verwaltungsvergütung"
                  value={(formData.shareClassColumns?.[konditionenTab]?.konditionen?.verwaltungsvergütung as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(konditionenTab, 'konditionen', 'verwaltungsvergütung', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Performance Fee"
                  value={(formData.shareClassColumns?.[konditionenTab]?.konditionen?.performance_fee as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(konditionenTab, 'konditionen', 'performance_fee', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label={KONDITIONEN_TER_FORM_LABEL}
                  value={(formData.shareClassColumns?.[konditionenTab]?.konditionen?.ter_2022 as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(konditionenTab, 'konditionen', 'ter_2022', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Ausgabeaufschlag"
                  value={(formData.shareClassColumns?.[konditionenTab]?.konditionen?.ausgabeaufschlag as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(konditionenTab, 'konditionen', 'ausgabeaufschlag', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Master Data (dual) */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              {renderSectionHeaderControls('stammdaten', { sectionId: 'stammdaten-section' })}
              {renderShareClassTabs(stammdatenTab, setStammdatenTab)}
              <Stack spacing={4}>
                <TextField
                  label="ISIN"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.isin as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'isin', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="WKN"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.wkn as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'wkn', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Bloomberg"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.bloomberg as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'bloomberg', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Reuters"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.reuters as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'reuters', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Fondsart"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.fondsart as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'fondsart', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Nachhaltigkeit"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.nachhaltigkeit as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'nachhaltigkeit', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Anlageschwerpunkt"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.anlageschwerpunkt as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'anlageschwerpunkt', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="SRI"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.srri as string) || ''}
                  onChange={(e) => handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'srri', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Vergleichsindex"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.vergleichsindex as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'vergleichsindex', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Auflagedatum"
                  type="date"
                  value={
                    formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.auflagedatum
                      ? toDateInputValue(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.auflagedatum as string)
                      : ''
                  }
                  onChange={(e) =>
                    handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'auflagedatum', e.target.value)
                  }
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                {templateShowsAnlageberater(templateId) ? (
                  <TextField
                    label="Anlageberater"
                    multiline
                    minRows={2}
                    maxRows={4}
                    value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.anlageberater as string) ?? ''}
                    onChange={(e) =>
                      handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'anlageberater', e.target.value)
                    }
                    onBlur={flushPendingParentSync}
                    helperText="Zeilenumbruch mit Enter (z. B. Tied-Agent-Zeile)."
                    fullWidth
                    size="small"
                  />
                ) : null}
                <TextField
                  label={getStammdatenFormLabel(templateId, 'verwaltungsgesellschaft', 'Verwaltungsgesellschaft')}
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.verwaltungsgesellschaft as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'verwaltungsgesellschaft', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Verwahrstelle"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.verwahrstelle as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'verwahrstelle', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Geschäftsjahr"
                  value={(formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.geschäftsjahr as string) || ''}
                  onChange={(e) =>
                    handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'geschäftsjahr', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Vertriebszulassung"
                  value={
                    formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.vertriebszulassung === undefined ||
                    formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.vertriebszulassung === null
                      ? DEFAULT_VERTREIBSZULASSUNG
                      : (formData.shareClassColumns?.[stammdatenTab]?.stammdaten?.vertriebszulassung as string)
                  }
                  onChange={(e) =>
                    handleShareClassColumnNested(stammdatenTab, 'stammdaten', 'vertriebszulassung', e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* Prices */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              {renderSectionHeaderControls('preise', { sectionId: 'preise-section' })}
              <Stack spacing={4}>
                <TextField
                  label="Währung"
                  value={formData.preise?.wahrung || ''}
                  onChange={(e) => handleNestedFieldChange('preise', 'wahrung', e.target.value)}
                  fullWidth
                  size="small"
                />
                {isPvmGps ? (
                  <>
                    {renderDynamicValueField('preise', 'ruecknahmepreis', 'Kurs (NAV)', '')}
                    {renderDynamicValueField('preise', 'fondsvolumen', 'Fondsvermögen', '')}
                  </>
                ) : (
                  <>
                    {renderDynamicValueField('preise', 'mepreis', 'Ausgabe-/Rücknahmepreis', '')}
                    {renderDynamicValueField('preise', 'fondsvolumen', 'Fondsvolumen', '')}
                  </>
                )}
                <TextField
                  label="Ertragsart"
                  placeholder="Thesaurierend"
                  value={formData.preise?.ertragsart || ''}
                  onChange={(e) => handleNestedFieldChange('preise', 'ertragsart', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Letzte Ausschüttung"
                  value={formData.preise?.letzte || ''}
                  onChange={(e) => handleNestedFieldChange('preise', 'letzte', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Ausschüttungstermin"
                  value={formData.preise?.ausschuttung || ''}
                  onChange={(e) => handleNestedFieldChange('preise', 'ausschuttung', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Conditions */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              {renderSectionHeaderControls('konditionen', { sectionId: 'konditionen-section' })}
              <Stack spacing={4}>
                <TextField
                  label="Laufende Kosten"
                  value={formData.konditionen?.laufendeKosten || ''}
                  onChange={(e) => handleNestedFieldChange('konditionen', 'laufendeKosten', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Verwaltungsgebühr"
                  value={formData.konditionen?.verwaltungsgebühr || ''}
                  onChange={(e) => handleNestedFieldChange('konditionen', 'verwaltungsgebühr', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Verwaltungsvergütung"
                  value={formData.konditionen?.verwaltungsvergütung || ''}
                  onChange={(e) => handleNestedFieldChange('konditionen', 'verwaltungsvergütung', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Performance Fee"
                  value={formData.konditionen?.performance_fee || ''}
                  onChange={(e) => handleNestedFieldChange('konditionen', 'performance_fee', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label={KONDITIONEN_TER_FORM_LABEL}
                  value={formData.konditionen?.ter_2022 || ''}
                  onChange={(e) => handleNestedFieldChange('konditionen', 'ter_2022', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Ausgabeaufschlag"
                  value={formData.konditionen?.ausgabeaufschlag || ''}
                  onChange={(e) => handleNestedFieldChange('konditionen', 'ausgabeaufschlag', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Master Data */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              {renderSectionHeaderControls('stammdaten', { sectionId: 'stammdaten-section' })}
              <Stack spacing={4}>
                <TextField
                  label="ISIN"
                  value={formData.stammdaten?.isin || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'isin', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="WKN"
                  value={formData.stammdaten?.wkn || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'wkn', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Bloomberg"
                  value={formData.stammdaten?.bloomberg || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'bloomberg', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Reuters"
                  value={formData.stammdaten?.reuters || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'reuters', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Fondsart"
                  value={formData.stammdaten?.fondsart || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'fondsart', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Nachhaltigkeit"
                  value={formData.stammdaten?.nachhaltigkeit || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'nachhaltigkeit', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Anlageschwerpunkt"
                  value={formData.stammdaten?.anlageschwerpunkt || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'anlageschwerpunkt', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="SRI"
                  value={formData.stammdaten?.srri || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'srri', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Vergleichsindex"
                  value={formData.stammdaten?.vergleichsindex || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'vergleichsindex', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Auflagedatum"
                  type="date"
                  value={formData.stammdaten?.auflagedatum ? toDateInputValue(formData.stammdaten.auflagedatum) : ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'auflagedatum', e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                {templateShowsAnlageberater(templateId) ? (
                  <TextField
                    label="Anlageberater"
                    multiline
                    minRows={2}
                    maxRows={4}
                    value={formData.stammdaten?.anlageberater ?? ''}
                    onChange={(e) => handleNestedFieldChange('stammdaten', 'anlageberater', e.target.value)}
                    onBlur={flushPendingParentSync}
                    helperText="Zeilenumbruch mit Enter (z. B. Tied-Agent-Zeile)."
                    fullWidth
                    size="small"
                  />
                ) : null}
                <TextField
                  label="Verwaltungsgesellschaft"
                  value={formData.stammdaten?.verwaltungsgesellschaft || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'verwaltungsgesellschaft', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Verwahrstelle"
                  value={formData.stammdaten?.verwahrstelle || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'verwahrstelle', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Geschäftsjahr"
                  value={formData.stammdaten?.geschäftsjahr || ''}
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'geschäftsjahr', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Vertriebszulassung"
                  value={
                    formData.stammdaten?.vertriebszulassung === undefined ||
                    formData.stammdaten?.vertriebszulassung === null
                      ? DEFAULT_VERTREIBSZULASSUNG
                      : formData.stammdaten.vertriebszulassung
                  }
                  onChange={(e) => handleNestedFieldChange('stammdaten', 'vertriebszulassung', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* History Chart Config */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('wertentwicklungSeitAuflage', { sectionId: 'wertentwicklung-seit-auflage-section' })}
          <Grid container spacing={2} rowSpacing={8} columnSpacing={8} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                label="ISIN (override)"
                placeholder="e.g. LU2439874319"
                value={formData.historyIsin || ''}
                onChange={(e) => handleFieldChange('historyIsin', e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Legend label"
                value={formData.historyLegendLabel ?? ''}
                onChange={(e) => handleFieldChange('historyLegendLabel', e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            {dualShareEnabled && (
              <Grid item xs={6}>
                <TextField
                  label="Performance (sub-label)"
                  placeholder="e.g. 227,77% / 6,64% p.a."
                  value={formData.historyLegendSubLabel ?? ''}
                  onChange={(e) => handleFieldChange('historyLegendSubLabel', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                label="Currency symbol (y-axis)"
                value={formData.historyCurrencySymbol ?? '€'}
                onChange={(e) => handleFieldChange('historyCurrencySymbol', e.target.value)}
                fullWidth
                size="small"
                helperText={
                  isPvmGps
                    ? 'Used on the seit-Auflage chart ticks. For BVI (indexed) use %; for a raw price chart use €.'
                    : undefined
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bar Chart - Wertentwicklung nach Jahren */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('wertentwicklungNachJahren', { sectionId: 'wertentwicklung-nach-jahren-section' })}

          <Box sx={{ my: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Select
                value={formData.rollingPerformance?.chartType || 'Automated'}
                onChange={(e) => {
                  flushPendingParentSync();
                  const newChartType = e.target.value;
                  const updatedData = {
                    ...formData,
                    rollingPerformance: {
                      ...formData.rollingPerformance,
                      chartType: newChartType,
                      ...(newChartType === 'Automated' && { automationType: 'all' }),
                    },
                  };
                  formDataRef.current = updatedData;
                  setFormData(updatedData);
                  onChange(updatedData);
                }}
                sx={{ minWidth: 150 }}
                size="small"
              >
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Automated">Automated</MenuItem>
              </Select>
            </Box>

            {formData.rollingPerformance?.chartType === 'Automated' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <OutlinedInput
                    value={formData.rollingPerformance?.isin || ''}
                    onChange={(e) => {
                      handleObjectFieldChange('rollingPerformance', 'isin', e.target.value);
                    }}
                    placeholder="ISIN"
                    size="small"
                    sx={{ width: 200 }}
                  />
                  <Select
                    value={formData.rollingPerformance?.automationType || 'all'}
                    onChange={(e) => {
                      handleObjectFieldChange('rollingPerformance', 'automationType', e.target.value);
                    }}
                    size="small"
                    sx={{ width: 200 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="gross_perf_year_as_of_today">Gross</MenuItem>
                    <MenuItem value="perf_year_as_of_today">Net</MenuItem>
                  </Select>
                </Box>
                {(!formData.rollingPerformance?.automationType || formData.rollingPerformance?.automationType === 'gross_perf_year_as_of_today') && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">Gross:</Typography>
                    <OutlinedInput
                      value={formData.rollingPerformance?.legend?.gross_perf_year_as_of_today || ''}
                      onChange={(e) => {
                        handleObjectFieldChange('rollingPerformance', 'legend', {
                          ...(formData.rollingPerformance?.legend || {}),
                          gross_perf_year_as_of_today: e.target.value,
                        });
                      }}
                      placeholder="e.g., brutto"
                      size="small"
                      sx={{ flex: 1, maxWidth: 400 }}
                    />
                  </Box>
                )}
                {formData.rollingPerformance?.automationType === 'perf_year_as_of_today' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">Net:</Typography>
                    <OutlinedInput
                      value={formData.rollingPerformance?.legend?.perf_year_as_of_today || ''}
                      onChange={(e) => {
                        handleObjectFieldChange('rollingPerformance', 'legend', {
                          ...(formData.rollingPerformance?.legend || {}),
                          perf_year_as_of_today: e.target.value,
                        });
                      }}
                      placeholder="e.g., netto (mit 5% Ausgabeaufschlag)"
                      size="small"
                      sx={{ flex: 1, maxWidth: 400 }}
                    />
                  </Box>
                )}
                {formData.rollingPerformance?.automationType === 'all' && (
                  <Box sx={{ display: 'flex', gap: 8 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="caption">Gross:</Typography>
                      <OutlinedInput
                        fullWidth
                        value={formData.rollingPerformance?.legend?.gross_perf_year_as_of_today || ''}
                        onChange={(e) => {
                          handleObjectFieldChange('rollingPerformance', 'legend', {
                            ...(formData.rollingPerformance?.legend || {}),
                            gross_perf_year_as_of_today: e.target.value,
                          });
                        }}
                        placeholder="e.g., brutto"
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="caption">Net:</Typography>
                      <OutlinedInput
                        fullWidth
                        value={formData.rollingPerformance?.legend?.perf_year_as_of_today || ''}
                        onChange={(e) => {
                          handleObjectFieldChange('rollingPerformance', 'legend', {
                            ...(formData.rollingPerformance?.legend || {}),
                            perf_year_as_of_today: e.target.value,
                          });
                        }}
                        placeholder="e.g., netto (mit 5% Ausgabeaufschlag)"
                        size="small"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {formData.rollingPerformance?.chartType !== 'Automated' && (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Manual data entries
                </Typography>
                <IconButton size="small" onClick={() => {
                  const periods = [...(formData.rollingPerformance?.periods || []), { label: '', value: 0 }];
                  handleObjectFieldChange('rollingPerformance', 'periods', periods);
                }} aria-label="Add period">
                  <Add fontSize="small" />
                </IconButton>
              </Box>
              <Stack spacing={8}>
                {(formData.rollingPerformance?.periods || []).map((period: any, index: number) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label={`Label ${index + 1}`}
                      value={period.label || ''}
                      onChange={(e) => {
                        const periods = [...(formData.rollingPerformance?.periods || [])];
                        periods[index] = { ...period, label: e.target.value };
                        handleObjectFieldChange('rollingPerformance', 'periods', periods);
                      }}
                      fullWidth
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Value"
                      type="number"
                      value={period.value ?? ''}
                      onChange={(e) => {
                        const periods = [...(formData.rollingPerformance?.periods || [])];
                        periods[index] = { ...period, value: parseFloat(e.target.value) || 0 };
                        handleObjectFieldChange('rollingPerformance', 'periods', periods);
                      }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                    <IconButton
                      onClick={() => {
                        const periods = [...(formData.rollingPerformance?.periods || [])];
                        periods.splice(index, 1);
                        handleObjectFieldChange('rollingPerformance', 'periods', periods);
                      }}
                      color="error"
                      size="small"
                      aria-label={`Remove period ${index + 1}`}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>

              {/* Bar chart label — SHV only; omitted on PVM (no Frankfurter footer under years chart) */}
              {!isPvmGps && (
                <TextField
                  label="Chart Label"
                  value={formData.wertentwicklung_nach_jahren_label || ''}
                  onChange={(e) => handleFieldChange('wertentwicklung_nach_jahren_label', e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 2 }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Performance Table (Wertentwicklung nach Perioden) */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('wertentwicklungNachPerioden', { sectionId: 'wertentwicklung-nach-perioden-section' })}
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={handleOpenPerformanceDialog}
                size="small"
              >
                Configure Table
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Performance Table Configuration Dialog */}
      <TableConfigurationDialog
        open={performanceDialogOpen}
        onClose={handleClosePerformanceDialog}
        onSave={handleSavePerformanceDialog}
        initialData={getPerformanceData()}
        title="Configure Performance Table"
        isin={isin}
      />

      {/* Risk Indicators Table (Risikoindikatoren) */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('risikoindikatoren', { sectionId: 'risikoindikatoren-section' })}
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={handleOpenRiskDialog}
                size="small"
              >
                Configure Table
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Risk Indicators Table Configuration Dialog */}
      <TableConfigurationDialog
        open={riskDialogOpen}
        onClose={handleCloseRiskDialog}
        onSave={handleSaveRiskDialog}
        initialData={getRiskData()}
        title="Configure Risk Indicators Table"
        isin={isin}
      />

      {/* Chart data — PVM page-2 allocations vs SHV pie charts */}
      {isPvmGps ? (
        <>
          {renderLabelValueEditor(
            'assetAllocation',
            sectionHeaders.assetAllocation || 'VERMÖGENSAUFTEILUNG',
          )}
          {renderLabelValueEditor(
            'sectorAllocation',
            sectionHeaders.sectorAllocation || 'BRANCHEN (AKTIENANTEIL)',
          )}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {sectionHeaders.top10Stocks || 'TOP-10 AKTIEN (in %)'}
                </Typography>
                <Tooltip title="Add stock">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const stocks = [...(formData.top10Stocks || []), { name: '', percentage: '' }];
                      handleFieldChange('top10Stocks', stocks);
                    }}
                    aria-label="Add top 10 stock"
                    disabled={(formData.top10Stocks || []).length >= 10}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              </Box>
              <Stack spacing={1}>
                {(formData.top10Stocks || []).map((stock: any, index: number) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      label={`Stock ${index + 1}`}
                      value={stock.name || ''}
                      onChange={(e) => {
                        const stocks = [...(formData.top10Stocks || [])];
                        stocks[index] = { ...stock, name: e.target.value };
                        handleFieldChange('top10Stocks', stocks);
                      }}
                      size="small"
                      sx={{ flex: 2 }}
                    />
                    <TextField
                      label="%"
                      value={stock.percentage ?? ''}
                      onChange={(e) => {
                        const stocks = [...(formData.top10Stocks || [])];
                        stocks[index] = { ...stock, percentage: e.target.value };
                        handleFieldChange('top10Stocks', stocks);
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const stocks = [...(formData.top10Stocks || [])];
                          stocks.splice(index, 1);
                          handleFieldChange('top10Stocks', stocks);
                        }}
                        aria-label={`Remove stock ${index + 1}`}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
                {!(formData.top10Stocks || []).length && (
                  <Typography variant="body2" color="text.secondary">
                    No holdings yet. Data loads from monthly allocations when available, or click + to add.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {renderLabelValueEditor('gesamtportfolio', configPieTitle('gesamtportfolio'))}
          {getPieChartConfigOrder(templateId).map((slot) =>
            renderLabelValueEditor(slot.field, configPieTitle(slot.key)),
          )}
        </>
      )}

      {/* Strategie */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('strategie', { sectionId: 'strategie-section' })}
          <TextField
            label="Strategie"
            value={formData.strategie || ''}
            onChange={(e) => handleFieldChange('strategie', e.target.value)}
            fullWidth
            multiline
            rows={4}
            size="small"
            sx={{ mt: 1 }}
          />
        </CardContent>
      </Card>

      {/* Chancen */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('chancen', { sectionId: 'chancen-section' })}
          <TextField
            label="Chancen"
            value={formData.chancen || ''}
            onChange={(e) => handleFieldChange('chancen', e.target.value)}
            fullWidth
            multiline
            rows={4}
            size="small"
            sx={{ mt: 1 }}
          />
        </CardContent>
      </Card>

      {/* Risiken */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('risiken', { sectionId: 'risiken-section' })}
          <TextField
            label="Risiken"
            value={formData.risiken || ''}
            onChange={(e) => handleFieldChange('risiken', e.target.value)}
            fullWidth
            multiline
            rows={4}
            size="small"
            sx={{ mt: 1 }}
          />
        </CardContent>
      </Card>

      {!omitEthikKontroverse && (
        <>
          {/* Ethik */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              {renderSectionHeaderControls('ethik', { sectionId: 'ethik-section' })}
              <TextField
                label="Ethik"
                value={formData.ethik || ''}
                onChange={(e) => handleFieldChange('ethik', e.target.value)}
                fullWidth
                multiline
                rows={4}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>

          {/* Business Table - Kontroverse Geschäftsfelder */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                {renderSectionHeaderControls('businessTable', { sectionId: 'business-table-section' })}
                <Tooltip title="Add row">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const currentTable = Array.isArray(formData.business_table) ? formData.business_table : [];
                      handleFieldChange('business_table', [
                        ...currentTable,
                        { label: '', no_turnover: '', sales_volume: '', image: '' },
                      ]);
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              </Box>
              {Array.isArray(formData.business_table) && formData.business_table.map((item: any, index: number) => {
                const exposureKeys = new Set(
                  (Array.isArray(formData.esg_exposure_flags) ? formData.esg_exposure_flags : []).map(
                    (f: { key?: string }) => f.key
                  )
                );
                const hasExposure = item.esg_key && exposureKeys.has(item.esg_key);
                return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 16,
                    mb: 1,
                    alignItems: 'center',
                    ...(hasExposure
                      ? { bgcolor: 'warning.light', borderRadius: 1, px: 1, py: 0.5 }
                      : {}),
                  }}
                >
                  <Tooltip title="Click to change icon">
                    <IconButton
                      size="small"
                      onClick={() => fileInputRefs.current[index]?.click()}
                      sx={{
                        width: 36,
                        height: 36,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        p: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      {getEsgItemImage(item, index) ? (
                        <img
                          src={getEsgItemImage(item, index)}
                          alt={item.label}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <ImageOutlined fontSize="small" color="disabled" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    ref={(el) => { fileInputRefs.current[index] = el; }}
                    onChange={handleImageUpload(index)}
                  />
                  <TextField
                    label="Label"
                    value={item.label || ''}
                    onChange={(e) => {
                      const updated = [...formData.business_table];
                      updated[index] = { ...updated[index], label: e.target.value };
                      handleFieldChange('business_table', updated);
                    }}
                    size="small"
                    sx={{ flex: 3 }}
                  />
                  <TextField
                    label="Kein Umsatz %"
                    value={item.no_turnover ?? ''}
                    onChange={(e) => {
                      const updated = [...formData.business_table];
                      updated[index] = { ...updated[index], no_turnover: e.target.value };
                      handleFieldChange('business_table', updated);
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Umsatz < 5%"
                    value={item.sales_volume ?? ''}
                    onChange={(e) => {
                      const updated = [...formData.business_table];
                      updated[index] = { ...updated[index], sales_volume: e.target.value };
                      handleFieldChange('business_table', updated);
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <Tooltip title="Remove row">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const updated = formData.business_table.filter((_: any, i: number) => i !== index);
                        handleFieldChange('business_table', updated);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
              })}
              <EsgFundImport
                formData={formData}
                onApplied={(patch) => {
                  const next = { ...formData, ...patch };
                  setFormData(next);
                  onChange(next);
                }}
              />
            </CardContent>
          </Card>
        </>
      )}
      {/* Cell Config Dialog for dynamic Preise fields */}
      {openCellConfigDialog && (
        <CellConfigDialog
          isin={isin}
          config={
            openCellConfigDialog.shareClassTab !== undefined && dualShareEnabled
              ? (formData as any)?.shareClassColumns?.[openCellConfigDialog.shareClassTab]?.[openCellConfigDialog.section]?.[openCellConfigDialog.field]
              : (formData as any)[openCellConfigDialog.section]?.[openCellConfigDialog.field]
          }
          open={!!openCellConfigDialog}
          onClose={handleCloseCellConfig}
          onConfirm={handleSetCellConfig}
        />
      )}

    </Box>
  );
};
